import React, { useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/hooks/useAuth";
import { useLessonData } from "@/hooks/useLessonData";
import { advanceStep, completeNode, recordXp } from "@/lib/courses/lessonProgress";
import { toast } from "@/hooks/use-toast";
import ConceptStep from "@/components/courses/ConceptStep";
import QuestionStep from "@/components/courses/QuestionStep";
import LessonComplete from "@/components/courses/LessonComplete";

const LessonPage: React.FC = () => {
  const { courseId = "", nodeId = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { node, steps, userProgress, isLoading } = useLessonData(courseId, nodeId);
  const [currentIdx, setCurrentIdx] = useState<number | null>(null);
  const [xpSession, setXpSession] = useState(0);
  const [completed, setCompleted] = useState(false);

  const isReviewMode = userProgress?.status === "completed";

  React.useEffect(() => {
    if (currentIdx === null && userProgress) {
      setCurrentIdx(isReviewMode ? 0 : userProgress.currentStepIndex);
    } else if (currentIdx === null && !isLoading && !userProgress) {
      setCurrentIdx(0);
    }
  }, [userProgress, isLoading, currentIdx, isReviewMode]);

  const totalSteps = steps.length;
  const currentStep = currentIdx !== null ? steps[currentIdx] : null;

  const conceptCounts = useMemo(() => {
    let idx = 0;
    return steps.map((s) => (s.type === "concept" ? ++idx : 0));
  }, [steps]);

  const questionCounts = useMemo(() => {
    let idx = 0;
    return steps.map((s) => (s.type === "question" ? ++idx : 0));
  }, [steps]);

  const totalConcepts = useMemo(
    () => steps.filter((s) => s.type === "concept").length,
    [steps]
  );

  const advance = useCallback(async () => {
    if (!user || currentIdx === null) return;
    const nextIdx = currentIdx + 1;
    if (nextIdx >= totalSteps) {
      if (!isReviewMode) {
        try {
          await completeNode(user.id, nodeId, courseId, xpSession);
          await recordXp(user.id, courseId, nodeId, 0, "lesson_complete");
        } catch {
          /* best-effort */
        }
        queryClient.invalidateQueries({ queryKey: ["user-node-progress"] });
        queryClient.invalidateQueries({ queryKey: ["user-course-progress"] });
        queryClient.invalidateQueries({ queryKey: ["weekly-xp"] });
      }
      setCompleted(true);
    } else {
      if (!isReviewMode) {
        try {
          await advanceStep(user.id, nodeId, nextIdx);
        } catch {
          /* best-effort */
        }
      }
      setCurrentIdx(nextIdx);
    }
  }, [user, currentIdx, totalSteps, nodeId, courseId, xpSession, queryClient, isReviewMode]);

  const handleCorrect = useCallback(
    async (xp: number) => {
      if (!user) return;
      if (isReviewMode) return;
      setXpSession((prev) => prev + xp);
      try {
        await recordXp(user.id, courseId, nodeId, xp, "question_correct");
      } catch {
        /* best-effort */
      }
      toast({ title: `+${xp} XP`, description: "Great job!" });
    },
    [user, courseId, nodeId, isReviewMode]
  );

  if (isLoading || currentIdx === null) {
    return (
      <DashboardShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse text-muted-foreground">Loading lesson…</div>
        </div>
      </DashboardShell>
    );
  }

  if (completed) {
    return (
      <DashboardShell>
        <div className="max-w-2xl mx-auto px-4 py-8">
          {isReviewMode ? (
            <div className="flex flex-col items-center justify-center text-center space-y-6 py-12">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
              >
                <CheckCircle2 className="text-primary" size={64} />
              </motion.div>
              <h2 className="text-2xl font-bold">Review Complete</h2>
              <p className="text-muted-foreground">
                You've reviewed all steps in this lesson.
              </p>
              <p className="text-xs text-muted-foreground">Your progress is safe</p>
              <Button onClick={() => navigate(`/course/${courseId}`)}>
                Back to Course
              </Button>
            </div>
          ) : (
            <LessonComplete
              title={node?.title ?? "Lesson"}
              xp={xpSession}
              onBack={() => navigate(`/course/${courseId}`)}
            />
          )}
        </div>
      </DashboardShell>
    );
  }

  const progressPct = totalSteps > 0 ? ((currentIdx + 1) / totalSteps) * 100 : 0;

  return (
    <DashboardShell>
      <div className="max-w-2xl mx-auto w-full px-4 py-6 space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/course/${courseId}`)}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <p className="text-lg font-medium truncate flex-1 min-w-0">{node?.title}</p>
        </div>

        <div className="flex items-center gap-3">
          <Progress value={progressPct} className="h-1.5 flex-1" />
          <span className="text-sm text-muted-foreground shrink-0">
            {currentIdx + 1} / {totalSteps}
          </span>
        </div>

        <AnimatePresence mode="wait">
          {currentStep && (
            <motion.div
              key={currentStep.id}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.25 }}
            >
              {currentStep.type === "concept" ? (
                <ConceptStep
                  step={currentStep}
                  conceptIndex={conceptCounts[currentIdx]}
                  totalConcepts={totalConcepts}
                  onContinue={advance}
                />
              ) : (
                <QuestionStep
                  key={currentStep.id}
                  step={currentStep}
                  questionNumber={questionCounts[currentIdx]}
                  onCorrect={handleCorrect}
                  onContinue={advance}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardShell>
  );
};

export default LessonPage;
